<script setup lang="ts">
import GeneratedPersonSearchTable from "./GeneratedPersonSearchTable.vue";
import { useDonationService } from "../../../cbi-generator/frontend/composables/useDonationFrontendService";
import type { DonationStaffSearchParams } from "../../../cbi-generator/generated/dto/DonationStaffSearchParams";
import type { DonationStaffSearchResult } from "../../../cbi-generator/generated/dto/DonationStaffSearchResult";
import { DonationStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/DonationStaffSearchParamsMeta";
import { DonationStaffSearchResultMeta } from "../../../cbi-generator/generated/dto/DonationStaffSearchResultMeta";
import type { DonationMemberSearchParams } from "../../../cbi-generator/generated/dto/DonationMemberSearchParams";
import type { DonationMemberSearchResult } from "../../../cbi-generator/generated/dto/DonationMemberSearchResult";
import { DonationMemberSearchParamsMeta } from "../../../cbi-generator/generated/dto/DonationMemberSearchParamsMeta";
import { DonationMemberSearchResultMeta } from "../../../cbi-generator/generated/dto/DonationMemberSearchResultMeta";
import { PurchasableState } from "../../../cbi-generator/generated/dto/PurchasableState";
import type { GeneratedTableConfig } from "@/composables/generated/contracts";
import { emptySearchParams } from "@/composables/useTableHelper";
import { useUtility } from "@/composables/useUtility";

const props = withDefaults(
  defineProps<{ personId: number; mode?: "staff" | "member" }>(),
  { mode: "staff" },
);
type Row = DonationStaffSearchResult | DonationMemberSearchResult;
const isMember = props.mode === "member";
const service = useDonationService();
const { formatDateNY } = useUtility();
const staffConfig: GeneratedTableConfig<
  DonationStaffSearchResult,
  DonationStaffSearchParams
> = {
  id: `person-${props.personId}-donations`,
  rowKey: "donationId",
  resultMeta: DonationStaffSearchResultMeta,
  searchMeta: DonationStaffSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<DonationStaffSearchParams>(
      DonationStaffSearchParamsMeta,
    ),
    personIdExact: props.personId,
    purchasableStateExact: PurchasableState.ACTIVE,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchDonationStaff(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  defaultSort: [{ key: "donationDate", order: "desc" }],
  visibleColumns: [
    "donationId",
    "price",
    "donationDate",
    "purchaseDate",
    "description",
    "restriction",
    "fundName",
    "initiativeName",
    "inMemoryOf",
    "isMatch",
    "inKind",
    "recurringDonationId",
  ],
};
// Member search is scoped to the caller's household server-side.
const memberConfig: GeneratedTableConfig<
  DonationMemberSearchResult,
  DonationMemberSearchParams
> = {
  ...staffConfig,
  id: `person-${props.personId}-donations-member`,
  rowKey: "donationId",
  resultMeta: DonationMemberSearchResultMeta,
  searchMeta: DonationMemberSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<DonationMemberSearchParams>(
      DonationMemberSearchParamsMeta,
    ),
    personIdExact: props.personId,
    purchasableStateExact: PurchasableState.ACTIVE,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchDonationMember(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: staffConfig.visibleColumns?.filter(
    (column) => column !== "purchaseDate" && column !== "recurringDonationId",
  ),
};
const config = (isMember ? memberConfig : staffConfig) as GeneratedTableConfig<
  Row,
  DonationMemberSearchParams
>;
const headers = [
  { key: "price", title: "Amount" },
  {
    key: "donationDate",
    title: "Donation Date",
    computeValue: (row: Row) => formatDateNY(row.donationDate),
  },
  ...(isMember
    ? []
    : [
        {
          key: "purchaseDate",
          title: "Purchase Date",
          computeValue: (row: Row) =>
            "purchaseDate" in row ? formatDateNY(row.purchaseDate) : "--",
        },
        { key: "recurringDonationId", title: "Recurring Donation" },
      ]),
];
</script>

<template>
  <GeneratedPersonSearchTable
    :config="config"
    :headers="headers"
    :filter-keys="[
      'descriptionLike',
      'fundNameLike',
      'initiativeNameLike',
      'donationDateGreaterEq',
      'donationDateLessEq',
    ]"
    refresh-flag="donationDataState"
  />
</template>
